import { tw } from '@careinfo/tailwind';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as XLSX from 'xlsx';
import { AddEmailCampaign } from '../../../utils/validationSchemas';
import AddEmailModal from '../../QuickEmail/AddEmailModal/AddEmailModal';
import Button from '../../Reusable/Button';
import Card from '../../Reusable/Card';
import MobilePreview from '../../Reusable/MobilePreview/MobilePreview';
import EmailCampaignForm from '../EmailCampaignForm/EmailCampaignForm';

const EmailCampaignCreate = ({ handleCreate, isPostLoading, refetch }) => {
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    clearErrors,
    setError,
    setValue,
    control,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(AddEmailCampaign),
    defaultValues: async () => {
      const res = await refetch();
      return {
        sender_email: res?.data?.[0],
      };
    },
  });

  const message = watch('content');
  const [isAddEmailModalOpen, setIsAddEmailModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePlusButton = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsAddEmailModalOpen(true);
  };

  const handleFileButton = (e: React.MouseEvent) => {
    e.preventDefault();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
      });

      const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
      const extractedEmails: string[] = jsonData
        .flat()
        .filter((cell) => typeof cell === 'string' && emailRegex.test(cell));

      const existing = watch('recipient') || [];
      const uniqueEmails = Array.from(
        new Set([...existing, ...extractedEmails])
      );

      setValue('recipient', uniqueEmails);
    } catch (err) {
      console.error('Failed to read Excel file', err);
    }
    e.target.value = '';
  };

  return (
    <div>
      <input
        type='file'
        ref={fileInputRef}
        accept='.xlsx,.xls'
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <div className={tw`pb-2`}>
        <h1 className={tw`text-lg font-semibold capitalize`}>
          Email Campaign Create
        </h1>
      </div>
      <div className={tw`pt-4`}>
        <div className={tw`min-h-[550px]`}>
          <Card>
            <div className={tw`flex flex-col lg:flex-row gap-4`}>
              <div className={tw`w-full lg:w-3/4`}>
                <form
                  onSubmit={handleSubmit((data) => {
                    handleCreate(data, setError, false);
                  })}
                >
                  <EmailCampaignForm
                    register={register}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    getValues={getValues}
                    clearErrors={clearErrors}
                    handlePlusButton={handlePlusButton}
                    control={control}
                    handleFileButton={handleFileButton}
                  />
                  <div className={tw`flex justify-end gap-x-4`}>
                    <div className={tw`w-40`}>
                      <Button
                        label='Save Draft'
                        type='button'
                        onClick={handleSubmit((data) =>
                          handleCreate(data, setError, true)
                        )}
                        isLoading={isPostLoading}
                      />
                    </div>
                    <div className={tw`w-40`}>
                      <Button
                        label={'Send Now'}
                        type='submit'
                        isLoading={isPostLoading}
                      />
                    </div>
                  </div>
                </form>
              </div>

              <div
                className={tw`hidden lg:flex items-center justify-center w-full lg:w-1/4`}
              >
                <div className='relative'>
                  <MobilePreview
                    imageUrl='https://dev-dib-pos-frontend-service-v3.s3.ap-south-1.amazonaws.com/digital-marketing-frontend-service/dist/30f7156109f80ad3a4d3.png'
                    description='Placeholders will be replaced for all contacts in a list. This is an example of the first contact.'
                    message={message}
                  />
                </div>
              </div>
            </div>
          </Card>

          <AddEmailModal
            isOpen={isAddEmailModalOpen}
            setIsOpen={setIsAddEmailModalOpen}
          />
        </div>
      </div>
    </div>
  );
};

export default EmailCampaignCreate;
